import { ICase, ICaseItem } from '@/utils/types/ICaseBattle';
import { CasebattlesState } from '@/stores/casebattles.store';
import { modalStore } from '@/stores/modal.store';
import { MouseEventHandler } from 'react';
import CaseImage from '@/components/CaseBattles/CaseImage';

interface Props {
    item: ICase;
    added: boolean;
    selected: CasebattlesState['selected'];
    setSelected: (selected: CasebattlesState['selected']) => void;
    closeRedirect?: boolean;
}

const CaseCard = ({ item, added, selected, setSelected, closeRedirect }: Props) => {
    const { setTitle, setComponent, setData, toggleModal } = modalStore((state) => state);

    const currentValue = selected.get(item.id) || 0;

    const next = (count: number) => {
        const newSelected = new Map(selected);
        if (count <= 0) newSelected.delete(item.id);
        else newSelected.set(item.id, count);
        setSelected(newSelected);
    };

    const plus = () => next(currentValue + 1);
    const minus = () => next(currentValue - 1);
    const del = () => next(0);
    const add = () => next(1);

    const openInfo: MouseEventHandler<HTMLElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setTitle(item.name);
        setComponent('Case');
        const data: { items: ICaseItem[]; onClose?: () => void } = { items: item.items };
        if (closeRedirect) {
            data.onClose = () => {
                setTitle('Add case');
                setComponent('Add case');
                setData({ tempSelected: selected });
            };
        }
        setData(data);
        toggleModal(true);
    };

    return (
        <div
            onContextMenu={openInfo}
            onClick={() => !added && add()}
            className={`relative h-[202px] rounded-fl-md p-2 flex flex-col cursor-pointer select-none transition-all hover:-translate-y-0.5 ${
                added
                    ? 'bg-accent_5/[.08] border border-accent_5/45 shadow-[0_8px_24px_-10px_rgba(255,194,77,0.40),inset_0_0_18px_-8px_rgba(255,194,77,0.25)] hover:border-accent_5/70'
                    : 'bg-layer_2 border border-stroke hover:bg-layer_3 hover:border-stroke_strong'
            }`}
        >
            <div className="flex justify-between mb-1">
                <button
                    type="button"
                    onClick={openInfo}
                    aria-label="View drops"
                    className="w-6 h-6 grid place-items-center rounded-full bg-layer_3 border border-stroke text-text_2 hover:text-text_main hover:bg-white/[0.13] transition-colors"
                >
                    <svg
                        viewBox="0 0 24 24"
                        className="w-3 h-3 stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round]"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m21 21-4.3-4.3" />
                    </svg>
                </button>
                {added && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            del();
                        }}
                        aria-label="Remove"
                        className="w-6 h-6 grid place-items-center rounded-full bg-layer_3 border border-stroke text-text_2 hover:text-danger hover:border-danger/40 hover:bg-danger/15 transition-colors"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            className="w-3 h-3 stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round]"
                        >
                            <path d="M6 6l12 12M18 6l-12 12" />
                        </svg>
                    </button>
                )}
            </div>

            <div className="flex-1 grid place-items-center">
                <div className="w-[88px] h-[88px]">
                    <CaseImage icon={item.icon} width={72} height={72} />
                </div>
            </div>

            <div className="text-[11px] text-text_3 text-center truncate mt-1">{item.name}</div>

            {!added ? (
                <button
                    type="button"
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-fl bg-layer_3 border border-stroke_strong text-text_main text-[12px] font-bold tracking-[0.04em] hover:bg-white/[0.13] hover:border-white/[0.22] transition-colors"
                >
                    <span className="text-text_3 font-medium">Add</span>
                    <span className="font-chat-nick text-accent_5 font-extrabold tabular-nums">
                        ${item.priceUsd.toFixed(2)}
                    </span>
                </button>
            ) : (
                <div className="mt-2 flex items-center gap-1">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            minus();
                        }}
                        aria-label="Decrease"
                        className="w-7 h-7 grid place-items-center rounded-fl bg-layer_3 border border-stroke text-accent_5 hover:bg-white/[0.13] transition-colors"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            className="w-3 h-3 stroke-current fill-none [stroke-width:2.4] [stroke-linecap:round]"
                        >
                            <path d="M5 12h14" />
                        </svg>
                    </button>
                    <div className="font-chat-nick flex-1 h-7 rounded-fl bg-layer_3 border border-stroke text-center grid place-items-center tabular-nums text-[13px] font-extrabold text-text_main">
                        {currentValue}
                    </div>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            plus();
                        }}
                        aria-label="Increase"
                        className="w-7 h-7 grid place-items-center rounded-fl bg-layer_3 border border-stroke text-accent_5 hover:bg-white/[0.13] transition-colors"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            className="w-3 h-3 stroke-current fill-none [stroke-width:2.4] [stroke-linecap:round]"
                        >
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                    </button>
                </div>
            )}

            {added && (
                <div className="font-chat-nick absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md text-[10px] font-extrabold tabular-nums text-[#6a4500]"
                    style={{
                        background: 'linear-gradient(135deg, #ffe08a, #ffc24d)',
                        boxShadow: '0 4px 10px rgba(255,194,77,0.45)',
                    }}
                >
                    ${item.priceUsd.toFixed(2)}
                </div>
            )}
        </div>
    );
};

export default CaseCard;
